/*
 * Hook (calls Apollo hooks — top-level use only): the shared diagram-upload flow for both
 * template editors. Mints a signed PUT, uploads, and holds the pending {imagePath, previewUrl}
 * per band key — a bandless (CUSTOM) template's document diagram rides the '' key — until the
 * caller's Save persists the path through updateFormatTemplate.
 */
import React, { useCallback, useState } from 'react';
import { buildIssueFormatTemplateDiagramUpload } from '../../lib/services/format-template.service';
import { uploadFileToSignedUrl } from '../../lib/utils/upload.utils';
import { IntlMsg } from '../_core/IntlMsg/intl-msg.component';
import { messages } from './format-template.messages';

export interface IPendingDiagram {
  imagePath: string;
  previewUrl: string;
}

const DOCUMENT_DIAGRAM_KEY = '';

export const useFormatTemplateDiagramUpload = (template: IFormatTemplate | null) => {
  const { issueFormatTemplateDiagramUpload } = buildIssueFormatTemplateDiagramUpload();

  const [pendingDiagrams, setPendingDiagrams] = useState<{ [bandKey: string]: IPendingDiagram }>({});
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<React.ReactNode>(null);

  const selectDiagram = useCallback(async (file: File, bandKey?: string) => {
    if (!template) return;
    const key = bandKey ?? DOCUMENT_DIAGRAM_KEY;
    setUploadError(null);
    setUploadingKey(key);
    try {
      const issued = await issueFormatTemplateDiagramUpload(template._id, file.type, bandKey);
      if (!issued) throw new Error('issueFormatTemplateDiagramUpload returned no capability');
      // PUT with the contentType the server signed for, not the local file.type — the signed
      // URL binds the header, so echoing the issued value can never mismatch the signature.
      await uploadFileToSignedUrl(issued.uploadUrl, file, issued.contentType);
      setPendingDiagrams(prev => {
        const previous = prev[key];
        if (previous) URL.revokeObjectURL(previous.previewUrl);
        return { ...prev, [key]: { imagePath: issued.imagePath, previewUrl: URL.createObjectURL(file) } };
      });
    } catch (err) {
      console.error('format template diagram upload failed:', err);
      setUploadError(<IntlMsg {...messages.diagramUploadError} />);
    } finally {
      setUploadingKey(null);
    }
  }, [template, issueFormatTemplateDiagramUpload]);

  // Drops every pending upload (revoking previews) — for the reset-on-open/template effects.
  const resetDiagrams = useCallback(() => {
    setPendingDiagrams(prev => {
      Object.values(prev).forEach(diagram => URL.revokeObjectURL(diagram.previewUrl));
      return {};
    });
    setUploadError(null);
  }, []);

  // Not memoized: a cheap keyed read; consumers depend on pendingDiagrams itself, not on this.
  const documentDiagram = pendingDiagrams[DOCUMENT_DIAGRAM_KEY] ?? null;

  return { pendingDiagrams, documentDiagram, uploadingKey, uploadError, selectDiagram, resetDiagrams };
};
